import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function CategoriesFilter() {
  const nav = useNavigate({ from: "/app/catalog/categories" });
  const search = useSearch({ from: "/app/catalog/categories" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const nameFilter = findCondition(filters, "name");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || ""
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value]
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      // Search across all language fields (lo, th, en) using OR group
      nextFilters = upsertOrGroup(filters, [
        { field: "name.lo", op: "contains", value: val },
        { field: "name.th", op: "contains", value: val },
        { field: "name.en", op: "contains", value: val },
        { field: "slug", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "name.lo");
      nextFilters = removeConditions(nextFilters, "name.th");
      nextFilters = removeConditions(nextFilters, "name.en");
      nextFilters = removeConditions(nextFilters, "slug");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  }, 400);

  const hasFilter = !!nameFilter?.value;

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-4 px-2"
      data-tourid="catalog-categories-filter"
    >
      <Input
        placeholder="Search categories (name or slug)..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="h-8 sm:max-w-2xs"
      />
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({
              search: {
                ...search,
                offset: 0,
                filters: undefined,
              } as any,
            })
          }
        >
          <XIcon className="h-4 w-4" /> Clear
        </Button>
      )}
    </div>
  );
}
